import React, { useState, useEffect } from "react";
import {
  Container,
  Button,
  Alert,
  Row,
  Col,
  Form,
  ProgressBar,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useUserProfiles } from "../contexts/UserProfileContext";
import {
  FaDice,
  FaArrowRight,
  FaLock,
  FaChild,
  FaCheckCircle,
  FaEye,
  FaEyeSlash,
} from "react-icons/fa";
import "./CreateProfile.css";

function CreateProfile() {
  const [currentStep, setCurrentStep] = useState(1);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showConfirmPin, setShowConfirmPin] = useState(false);

  // Parent account data
  const [parentData, setParentData] = useState({
    pin: "",
    confirmPin: "",
  });

  // Child profile data
  const [profileData, setProfileData] = useState({
    name: "",
    avatar: `https://api.dicebear.com/6.x/fun-emoji/svg?seed=${Date.now()}`,
  });

  const { user, savePIN } = useUser();
  const {
    createProfile,
    profiles,
    isLoading: profilesLoading,
  } = useUserProfiles();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) {
      navigate("/signin", { replace: true });
    } else if (!profilesLoading && profiles.length > 0) {
      navigate("/games", { replace: true });
    }
  }, [user, profiles, profilesLoading, navigate]);

  const handleRandomizeAvatar = () => {
    setProfileData((prev) => ({
      ...prev,
      avatar: `https://api.dicebear.com/6.x/fun-emoji/svg?seed=${Date.now()}`,
    }));
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!parentData.pin.trim()) {
      setError("Please create a PIN");
      return;
    }

    if (parentData.pin.length < 4) {
      setError("PIN must be at least 4 characters");
      return;
    }

    if (parentData.pin !== parentData.confirmPin) {
      setError("PINs do not match");
      return;
    }

    try {
      setIsLoading(true);
      console.log("Saving PIN for user:", user.email);

      // Save PIN to Firestore (hashed)
      await savePIN(parentData.pin);

      console.log("PIN saved successfully, proceeding to profile creation");
      setCurrentStep(2);
    } catch (err) {
      console.error("Error saving PIN:", err);
      setError("Failed to save PIN. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profileData.name.trim()) {
      setError("Please enter a name for your child's profile");
      return;
    }

    setError("");
    setIsLoading(true);

    try {
      const newProfile = await createProfile({
        name: profileData.name.trim(),
        avatar: profileData.avatar,
      });

      if (newProfile) {
        setCurrentStep(3);
        // Auto-redirect after success
        setTimeout(() => {
          navigate("/games", { replace: true });
        }, 2000);
      } else {
        throw new Error("Failed to create profile");
      }
    } catch (err) {
      setError(err.message);
      setIsLoading(false);
    }
  };

  if (profilesLoading) {
    return (
      <div className="create-profile-page d-flex align-items-center justify-content-center">
        <div className="spinner-border text-light" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div className="step-header text-center mb-4">
              <FaLock className="step-icon mb-3" />
              <h2>Set Up Parent Account</h2>
              <p className="step-description">
                Welcome to Star Launch! First, let's secure your account with a
                PIN that will be required for any account changes.
              </p>
            </div>

            <Form onSubmit={handlePinSubmit}>
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Create Account PIN</Form.Label>
                    <div className="pin-input-wrapper">
                      <Form.Control
                        type={showPin ? "text" : "password"}
                        placeholder="Enter 4+ digit PIN"
                        value={parentData.pin}
                        onChange={(e) =>
                          setParentData((prev) => ({
                            ...prev,
                            pin: e.target.value,
                          }))
                        }
                        className="pin-input"
                        autoFocus
                      />
                      <Button
                        variant="outline-secondary"
                        className="pin-toggle-btn"
                        onClick={() => setShowPin(!showPin)}
                        type="button"
                      >
                        {showPin ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                    </div>
                    <Form.Text className="text-muted">
                      This PIN will be required to make changes to your account
                      or subscription
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-4">
                    <Form.Label>Confirm PIN</Form.Label>
                    <div className="pin-input-wrapper">
                      <Form.Control
                        type={showConfirmPin ? "text" : "password"}
                        placeholder="Confirm your PIN"
                        value={parentData.confirmPin}
                        onChange={(e) =>
                          setParentData((prev) => ({
                            ...prev,
                            confirmPin: e.target.value,
                          }))
                        }
                        className="pin-input"
                      />
                      <Button
                        variant="outline-secondary"
                        className="pin-toggle-btn"
                        onClick={() => setShowConfirmPin(!showConfirmPin)}
                        type="button"
                      >
                        {showConfirmPin ? <FaEyeSlash /> : <FaEye />}
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" className="w-100 next-step-btn">
                Next: Create Child Profile <FaArrowRight className="ms-2" />
              </Button>
            </Form>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div className="step-header text-center mb-4">
              <FaChild className="step-icon mb-3" />
              <h2>Create Your Child's Profile</h2>
              <p className="step-description">
                Now let's create your child's gaming profile. You can add more
                profiles later!
              </p>
            </div>

            <Form onSubmit={handleProfileSubmit}>
              {error && (
                <Alert variant="danger" className="mb-4">
                  {error}
                </Alert>
              )}

              <div className="avatar-section text-center mb-4">
                <img
                  src={profileData.avatar}
                  alt="Profile avatar"
                  className="profile-avatar mb-3"
                  style={{ width: "120px", height: "120px" }}
                  onClick={handleRandomizeAvatar}
                />

                <div>
                  <Button
                    type="button"
                    variant="outline-primary"
                    onClick={handleRandomizeAvatar}
                    className="randomize-avatar-btn d-flex align-items-center mx-auto"
                    disabled={isLoading}
                    style={{ pointerEvents: "auto", cursor: "pointer" }}
                  >
                    <FaDice className="me-2" /> New Avatar
                  </Button>
                </div>
              </div>

              <Form.Group className="mb-4">
                <Form.Label>Child's Name</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your child's name"
                  value={profileData.name}
                  onChange={(e) =>
                    setProfileData((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  disabled={isLoading}
                  className="profile-name-input"
                />
              </Form.Group>

              <Button
                type="submit"
                className="w-100 create-profile-btn"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                ) : (
                  <span className="d-flex align-items-center justify-content-center">
                    Complete Setup <FaArrowRight className="ms-2" />
                  </span>
                )}
              </Button>
            </Form>
          </div>
        );

      case 3:
        return (
          <div className="step-content text-center">
            <div className="success-content">
              <FaCheckCircle className="success-icon mb-4" />
              <h2 className="mb-3">All Set!</h2>
              <p className="success-message mb-4">
                Your account is ready! {profileData.name}'s profile has been
                created and you'll be redirected to the games in just a moment.
              </p>
              <div className="success-details">
                <div className="created-profile-card">
                  <img
                    src={profileData.avatar}
                    alt={profileData.name}
                    className="success-avatar"
                  />
                  <h5>{profileData.name}</h5>
                  <small className="text-muted">Ready to play!</small>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const getProgressPercentage = () => {
    switch (currentStep) {
      case 1:
        return 33;
      case 2:
        return 66;
      case 3:
        return 100;
      default:
        return 0;
    }
  };

  return (
    <div className="create-profile-page">
      <Container className="welcome-container">
        <Row className="justify-content-center">
          <Col xs={12} sm={10} md={8} lg={6}>
            <div className="welcome-content">
              <div className="progress-section mb-4">
                <ProgressBar
                  now={getProgressPercentage()}
                  className="setup-progress"
                  variant="primary"
                />
                <div className="step-indicators">
                  <span
                    className={`step-number ${
                      currentStep >= 1 ? "active" : ""
                    }`}
                  >
                    1
                  </span>
                  <span
                    className={`step-number ${
                      currentStep >= 2 ? "active" : ""
                    }`}
                  >
                    2
                  </span>
                  <span
                    className={`step-number ${
                      currentStep >= 3 ? "active" : ""
                    }`}
                  >
                    3
                  </span>
                </div>
              </div>

              {renderStepContent()}
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}

export default CreateProfile;
